import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Search, Clock, Phone, Navigation, Loader2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { dialloStores, abidjanNeighborhoods } from '../data/dialloData';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom Diallo marker icon - Red per brand guidelines
const dialloIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ED1C24" width="36" height="36">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

// User location marker
const userIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3B82F6" width="24" height="24">
      <circle cx="12" cy="12" r="8" fill="#3B82F6" stroke="white" stroke-width="3"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});

// Component to recenter map
const RecenterMap = ({ center }) => {
  const map = useMap();
  React.useEffect(() => {
    if (center) {
      map.setView(center, 14);
    }
  }, [center, map]);
  return null;
};

const DialloPage = () => {
  const { language } = useLanguage();
  const t = language === 'fr';
  
  const [searchNeighborhood, setSearchNeighborhood] = useState('');
  const [filteredStores, setFilteredStores] = useState(dialloStores);
  const [selectedStore, setSelectedStore] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);

  // Filter stores by neighborhood search
  const handleSearch = (value) => {
    setSearchNeighborhood(value);
    if (!value.trim()) {
      setFilteredStores(dialloStores);
    } else {
      const filtered = dialloStores.filter(store => 
        store.neighborhood.toLowerCase().includes(value.toLowerCase()) ||
        store.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredStores(filtered);
    }
  };

  // Geolocation
  const requestGeolocation = () => {
    if (!navigator.geolocation) return;
    
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setGeoLoading(false);
      },
      () => {
        setGeoLoading(false);
      }
    );
  };

  const abidjanCenter = [5.3364, -4.0267];
  const mapCenter = userLocation ? [userLocation.lat, userLocation.lng] : abidjanCenter;

  return (
    <div className="min-h-screen bg-[#F2E9D6] dark:bg-[#0F1A14]">
      {/* Hero Section - Diallo Red Theme */}
      <section className="relative py-12 md:py-16 bg-gradient-to-br from-[#ED1C24] via-[#ED1C24] to-[#C41920] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')]" />
        </div>
        
        <div className="container relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <img 
                  src="/brand/diallo_logo_white.svg"
                  alt="Diallo"
                  className="h-12 md:h-14 mx-auto md:mx-0 mb-3"
                />
                <span className="inline-block px-3 py-1 bg-white/20 text-white text-sm rounded-full">
                  {t ? 'Réseau de distribution Océana' : 'Océana distribution network'}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                {t ? 'Nos produits près de chez vous' : 'Our products near you'}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-white/90 mb-6 max-w-md mx-auto md:mx-0"
              >
                {t 
                  ? 'Retrouvez les fruits et légumes frais Océana dans les boutiques Diallo à travers Abidjan.'
                  : 'Find fresh Océana fruits and vegetables in Diallo stores across Abidjan.'}
              </motion.p>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={requestGeolocation}
                disabled={geoLoading}
                className="inline-flex items-center gap-2 bg-white text-[#ED1C24] font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {geoLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Navigation size={18} />
                )}
                {t ? 'Trouver une boutique' : 'Find a store'}
              </motion.button>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="hidden md:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1771019992524-9d83e1bf69bb?auto=format&fit=crop&w=600&q=80"
                alt="Diallo Store"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-8 md:py-12">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Store List */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="bg-white dark:bg-[#1A2B22] rounded-2xl p-4 shadow-sm">
                <h2 className="text-lg font-bold text-[#ED1C24] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {t ? 'Boutiques Diallo' : 'Diallo Stores'}
                </h2>
                
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder={t ? 'Rechercher un quartier...' : 'Search neighborhood...'}
                    value={searchNeighborhood}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#ED1C24] text-sm"
                  />
                </div>

                {/* Store List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredStores.map((store) => (
                    <button
                      key={store.id}
                      onClick={() => setSelectedStore(store)}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        selectedStore?.id === store.id
                          ? 'bg-[#ED1C24] text-white'
                          : 'bg-gray-50 dark:bg-[#0F1A14] hover:bg-[#ED1C24]/10'
                      }`}
                    >
                      <h3 className={`font-semibold text-sm ${selectedStore?.id === store.id ? 'text-white' : 'text-[#231F20] dark:text-white'}`}>
                        {store.name}
                      </h3>
                      <p className={`text-xs ${selectedStore?.id === store.id ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        {store.address}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="bg-white dark:bg-[#1A2B22] rounded-2xl overflow-hidden shadow-sm h-[400px] md:h-[500px]">
                <MapContainer
                  center={abidjanCenter}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {userLocation && <RecenterMap center={[userLocation.lat, userLocation.lng]} />}
                  {selectedStore && <RecenterMap center={selectedStore.coordinates} />}
                  
                  {userLocation && (
                    <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                      <Popup>{t ? 'Vous êtes ici' : 'You are here'}</Popup>
                    </Marker>
                  )}
                  
                  {filteredStores.map((store) => (
                    <Marker 
                      key={store.id}
                      position={store.coordinates}
                      icon={dialloIcon}
                      eventHandlers={{
                        click: () => setSelectedStore(store)
                      }}
                    >
                      <Popup>
                        <div className="p-1">
                          <h3 className="font-bold text-[#ED1C24]">{store.name}</h3>
                          <p className="text-xs text-gray-600">{store.address}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            <Clock size={12} className="inline mr-1" />
                            {store.openHours}
                          </p>
                          <a 
                            href={`tel:${store.phone}`}
                            className="text-xs text-[#ED1C24] hover:underline mt-1 block"
                          >
                            <Phone size={12} className="inline mr-1" />
                            {store.phone}
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>
          </div>

          {/* Selected Store Details */}
          {selectedStore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-white dark:bg-[#1A2B22] rounded-2xl p-6 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-[#ED1C24]">{selectedStore.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{selectedStore.address}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {selectedStore.openHours}
                    </span>
                    <a href={`tel:${selectedStore.phone}`} className="flex items-center gap-1 text-[#ED1C24] hover:underline">
                      <Phone size={14} />
                      {selectedStore.phone}
                    </a>
                  </div>
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${selectedStore.coordinates[0]},${selectedStore.coordinates[1]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C41920] text-white font-semibold px-5 py-2.5 rounded-full transition-colors"
                >
                  <MapPin size={18} />
                  {t ? 'Itinéraire' : 'Directions'}
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Océana Quality Banner */}
      <section className="py-8 bg-[#1A5C38]">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_e0d36ff9-afe9-42cc-acb6-596aa3a8fe6f/artifacts/lt33l9w1_6FA12A78-938C-4DE4-9159-597D78E23F7F.png"
              alt="Océana"
              className="h-8 brightness-0 invert"
            />
            <p className="text-white/90 text-sm">
              {t 
                ? 'Tous les produits Diallo sont sélectionnés et livrés par Océana — la même qualité que dans les grandes surfaces.'
                : 'All Diallo products are selected and delivered by Océana — the same quality as in supermarkets.'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DialloPage;
